import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useMask } from '@atlas/react-forms';
import { FieldHint, Input } from '@atlas/ui';

import Field from 'common/Form/Field';

const TextField = ({ className, field, onBlur, onChange, onFocus }) => {
  const {
    active,
    autocomplete,
    autofocus,
    disabled,
    errors,
    hint,
    isModifiable = true,
    label,
    maskOption,
    name,
    placeholder,
    touched,
    trim,
    value,
    warnings,
  } = field;

  const hasMask = Boolean(maskOption);

  const {
    handleChange: handleMaskChange,
    handleKeyDown,
    maskedValue,
    value: cleanValue,
  } = useMask({
    mask: maskOption,
  });
  const [activeField, setActiveField] = useState(active);

  const handleDirectChange = useCallback(
    e => {
      const newValue = trim ? e.target.value.trim() : e.target.value;
      onChange(newValue);
    },
    [onChange, trim],
  );

  const error = touched && errors[0];
  const success = touched && !error;
  const warning = touched ? warnings?.[0]?.message : null;

  useEffect(() => {
    if (!hasMask) return;
    const effectiveCleanValue = trim ? cleanValue.trim() : cleanValue;
    if (value && value !== effectiveCleanValue) {
      handleMaskChange({
        target: {
          selectionStart: String(value).length,
          value: String(value),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMaskChange, hasMask, value, trim]);

  useEffect(() => {
    if (!hasMask) return;
    onChange(trim ? cleanValue.trim() : cleanValue);
  }, [cleanValue, hasMask, onChange, trim]);

  return (
    <Field isSignupForm options={field}>
      <Input
        active={activeField}
        autoComplete={autocomplete}
        autoFocus={autofocus}
        className={className}
        disabled={disabled || !isModifiable}
        error={error}
        label={label}
        name={name}
        onBlur={() => {
          setActiveField(false);
          onBlur();
        }}
        onChange={hasMask ? handleMaskChange : handleDirectChange}
        onFocus={() => {
          setActiveField(true);
          onFocus();
        }}
        onKeyDown={hasMask ? handleKeyDown : undefined}
        placeholder={maskOption || placeholder}
        success={success}
        touched={touched}
        value={hasMask ? maskedValue : value || ''}
        warning={warning}
      />
      {!disabled && <FieldHint hint={hint} />}
    </Field>
  );
};

TextField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.shape({
    active: PropTypes.bool,
    autocomplete: PropTypes.string,
    autofocus: PropTypes.bool,
    disabled: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.object),
    hint: PropTypes.string,
    isModifiable: PropTypes.bool,
    label: PropTypes.string,
    maskOption: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    touched: PropTypes.bool,
    trim: PropTypes.bool,
    value: PropTypes.string,
    warnings: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool])),
  }),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
};

export default TextField;
